import { createContext, createElement, ReactNode, useCallback, useContext, useMemo, useState } from "react";
import { api } from "../api/client";
import type { LoginResponse, Operator } from "../api/types";

const KEY = "insp.session";

export interface Session {
  token: string;
  operator: Operator;
}

export function readSession(): Session | null {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "null");
  } catch {
    return null;
  }
}

function writeSession(s: Session | null) {
  if (s) localStorage.setItem(KEY, JSON.stringify(s));
  else localStorage.removeItem(KEY);
}

export function getStoredToken(): string | null {
  return readSession()?.token ?? null;
}

export function clearStoredSession() {
  writeSession(null);
}

interface SessionCtx {
  session: Session | null;
  login: (username: string, pin: string) => Promise<Operator>;
  logout: () => void;
  refreshMe: () => Promise<Operator | null>;
}

const Ctx = createContext<SessionCtx>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(readSession);

  const login = useCallback(async (username: string, pin: string) => {
    const r = await api.post<LoginResponse>("/auth/login", { username, pin });
    const s = { token: r.token, operator: r.operator };
    writeSession(s);
    setSession(s);
    return r.operator;
  }, []);

  const logout = useCallback(() => {
    writeSession(null);
    setSession(null);
  }, []);

  const refreshMe = useCallback(async () => {
    const current = readSession();
    if (!current) return null;
    const op = await api.get<Operator>("/auth/me");
    const s = { token: current.token, operator: op };
    writeSession(s);
    setSession(s);
    return op;
  }, []);

  const value = useMemo(() => ({ session, login, logout, refreshMe }), [session, login, logout, refreshMe]);
  return createElement(Ctx.Provider, { value }, children);
}

export const useSession = () => useContext(Ctx);
